import React from 'react';
import { makeStyles, Theme, Grid, Paper } from '@material-ui/core';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import { CatalogIcon, DocsIcon } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { PremiumCard, SectionHeader } from '../ui';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  filters: {
    padding: 0,
    marginTop: theme.spacing(2),
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
  root: {
    width: '100%',
  },
}));

const SearchPage = () => {
  const classes = useStyles();
  const { types } = useSearch();
  const catalogApi = useApi(catalogApiRef);

  return (
    <Grid container spacing={3} className={classes.root}>
      <Grid item xs={12}>
        <PremiumCard
          title="Unified Search"
          subheader="Catalog + TechDocs in one place"
        >
          <SectionHeader
            title="Search Workspace"
            subtitle="Filter quickly across components, templates, and documentation"
          />
          <Paper className={classes.bar}>
            <SearchBar />
          </Paper>
        </PremiumCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <PremiumCard title="Filters">
          <SearchType.Accordion
            name="Result Type"
            defaultValue="software-catalog"
            types={[
              {
                value: 'software-catalog',
                name: 'Software Catalog',
                icon: <CatalogIcon />,
              },
              {
                value: 'techdocs',
                name: 'Documentation',
                icon: <DocsIcon />,
              },
            ]}
          />
          <Paper className={classes.filters}>
            {types.includes('techdocs') && (
              <SearchFilter.Select
                className={classes.filter}
                label="Entity"
                name="name"
                values={async () => {
                  const { items } = await catalogApi.getEntities({
                    fields: ['metadata.name'],
                    filter: {
                      'metadata.annotations.backstage.io/techdocs-ref':
                        CATALOG_FILTER_EXISTS,
                    },
                  });

                  const names = items.map(entity => entity.metadata.name);
                  names.sort();
                  return names;
                }}
              />
            )}
            <SearchFilter.Select
              className={classes.filter}
              label="Kind"
              name="kind"
              values={['Component', 'Template']}
            />
            <SearchFilter.Checkbox
              className={classes.filter}
              label="Lifecycle"
              name="lifecycle"
              values={['experimental', 'production']}
            />
          </Paper>
        </PremiumCard>
      </Grid>

      <Grid item xs={12} md={8}>
        <PremiumCard title="Results">
          <SearchPagination />
          <SearchResult>
            <CatalogSearchResultListItem icon={<CatalogIcon />} />
            <TechDocsSearchResultListItem icon={<DocsIcon />} />
          </SearchResult>
        </PremiumCard>
      </Grid>
    </Grid>
  );
};

export const searchPage = <SearchPage />;
